import { useContext, useState } from 'react';

import { Or2LoadingButton } from '@/components/button/Or2LoadingButton';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import { graphql, type DocumentType } from '@/gql';
import { useMutation } from '@/hooks/mutationWithSnackbar';
import { CurrentUserContext } from '@/context/CurrentUserContext';
import { removePendingInviteFromCache } from '@/modules/invitations/utils/pendingInvitesCache';

const ACCEPT_PENDING_MEMBER_INVITE_MUTATION = graphql(`
  mutation AcceptPendingMemberInvite($params: AcceptPendingMemberInviteInput) {
    acceptPendingMemberInvite(params: $params) {
      group {
        _id
      }
    }
  }
`);

const ACCEPT_PENDING_INVITE_BUTTON_GROUP_FRAGMENT = graphql(`
  fragment acceptPendingInviteButtonGroupFields on Group {
    _id
    name
  }
`);

interface Props {
  group: DocumentType<typeof ACCEPT_PENDING_INVITE_BUTTON_GROUP_FRAGMENT>;
}

export const AcceptPendingInviteButton = ({ group }: Props) => {
  const { _id: groupId, name: groupName } = group;
  const { currentUserId } = useContext(CurrentUserContext);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [acceptPendingMemberInvite, { loading }] = useMutation(
    ACCEPT_PENDING_MEMBER_INVITE_MUTATION,
    {
      completionMessage: `You're now a member of ${groupName}`,
      update: (cache) => {
        removePendingInviteFromCache(cache, currentUserId, groupId);
      },
      onCompleted: () => setConfirmOpen(false),
      onError: () => setConfirmOpen(false),
    },
  );

  return (
    <>
      <Or2LoadingButton
        size="small"
        loading={loading}
        onClick={() => setConfirmOpen(true)}
      >
        Accept
      </Or2LoadingButton>
      <ConfirmDialog
        open={confirmOpen}
        ariaLabel="accept-pending-invite"
        titleText={`Join ${groupName}?`}
        innerText={`Once you join, the admins of ${groupName} will be able to view and edit your profile details, and its members will be able to see your profile.`}
        confirmText="Accept invitation"
        loading={loading}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() =>
          acceptPendingMemberInvite({ variables: { params: { groupId } } })
        }
      />
    </>
  );
};
